import React, { useState, useMemo, useEffect } from 'react';
import { Box, Card, Typography, Button, TextField, MenuItem, Alert, Tooltip, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  RefreshRounded as RefreshIcon, StorefrontRounded as BookmakerIcon, TrendingUpRounded as ValueIcon,
  KeyRounded as KeyIcon, InfoOutlined as InfoIcon, WarningAmberRounded as WarnIcon
} from '@mui/icons-material';
import { apiCall } from '../config';
import { useApi } from '../hooks';
import { tokens } from '../theme';
import { PageHeader, CardGridSkeleton, EmptyState, ErrorState, OutcomePill, cardGridSx, formatDay, formatTime, pct } from './ui';
import BetsTabs from './BetsTabs';
import BookmakerPicker from './BookmakerPicker';

const SIZING = {
  quarter: { label: '¼ Kelly', stake: (bankroll, f) => bankroll * f * 0.25 },
  half: { label: '½ Kelly', stake: (bankroll, f) => bankroll * f * 0.5 },
  flat: { label: 'Flat 1%', stake: (bankroll) => bankroll * 0.01 },
};

// Per-viewer convenience only; falls back to defaults if storage is unavailable
const readPref = (key, fallback) => {
  try { const v = localStorage.getItem(key); return v === null ? fallback : JSON.parse(v); } catch { return fallback; }
};
const writePref = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ } };

const money = (n) => Number(n).toLocaleString(undefined, { maximumFractionDigits: n >= 100 ? 0 : 2 });

function ValueCard({ bet, bankroll, sizing }) {
  const team = bet.outcome === 'home' ? bet.home_team : bet.outcome === 'away' ? bet.away_team : 'Draw';
  // Kelly fraction: the share of bankroll that maximises long-run growth for this edge
  const kelly = bet.edge / (bet.price - 1);
  const stake = Math.max(0, SIZING[sizing].stake(bankroll, kelly));
  const expectedProfit = stake * bet.edge;

  return (
    <Card sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', borderColor: bet.suspicious ? alpha(tokens.status.warn, 0.45) : tokens.border }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1.5} mb={1.5}>
        <Box minWidth={0}>
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {bet.sport} · {formatDay(bet.commence_time)} {formatTime(bet.commence_time)}
          </Typography>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
            {bet.home_team} <Box component="span" color="text.secondary" fontWeight={500}>vs</Box> {bet.away_team}
          </Typography>
        </Box>
        <Box textAlign="right" flexShrink={0}>
          <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: '1.35rem', lineHeight: 1.1, color: tokens.status.good }}>
            +{(bet.edge * 100).toFixed(1)}%
          </Typography>
          <Typography variant="caption" color="text.secondary">expected value</Typography>
        </Box>
      </Box>

      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: tokens.surfaceRaised, border: `1px solid ${tokens.border}`, mb: 1.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
          <Box minWidth={0}>
            <OutcomePill outcome={bet.outcome} label={bet.outcome === 'draw' ? 'Draw' : `${team} win`} />
            <Typography variant="caption" color="text.secondary" display="block" mt={0.75} noWrap>
              at <Box component="span" color="text.primary" fontWeight={600}>{bet.bookmaker}</Box>
              {bet.other_bookmakers > 0 && ` · also at ${bet.other_bookmakers} other${bet.other_bookmakers === 1 ? '' : 's'}`}
            </Typography>
          </Box>
          <Box textAlign="right" flexShrink={0}>
            <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.1 }}>
              {bet.quoted.toFixed(2)}
            </Typography>
            <Typography variant="caption" color="text.secondary">fair {bet.fair_odds.toFixed(2)}</Typography>
          </Box>
        </Box>
      </Box>

      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} mb={1.5}>
        <Box>
          <Typography variant="overline" color="text.secondary" display="block" lineHeight={1.4}>True chance</Typography>
          <Typography variant="body2" fontWeight={600}>{pct(bet.true_probability, 1)}</Typography>
        </Box>
        <Box>
          <Typography variant="overline" color="text.secondary" display="block" lineHeight={1.4}>Price implies</Typography>
          <Typography variant="body2" fontWeight={600}>{pct(1 / bet.price, 1)}</Typography>
        </Box>
      </Box>

      {bet.commission > 0 && (
        <Typography variant="caption" color="warning.main" display="block" mb={1}>
          Exchange: value is after {Math.round(bet.commission * 100)}% commission (net odds {bet.price.toFixed(2)}).
        </Typography>
      )}
      {bet.suspicious && (
        <Box display="flex" gap={0.75} alignItems="flex-start" mb={1} sx={{ color: tokens.status.warn }}>
          <WarnIcon sx={{ fontSize: 16, mt: '2px' }} />
          <Typography variant="caption">
            {bet.suspicious_reason === 'reference_outlier'
              ? `Most bookmakers beat ${bet.reference} here, so its price is probably out of date rather than this being real value.`
              : 'Unusually big edge. Often a stale price or a mistake, so check it at the bookmaker first.'}
          </Typography>
        </Box>
      )}

      <Box mt="auto" display="flex" justifyContent="space-between" alignItems="flex-end" gap={1} pt={1} sx={{ borderTop: `1px solid ${tokens.border}` }}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">Suggested stake ({SIZING[sizing].label})</Typography>
          <Typography fontWeight={700}>{money(stake)}</Typography>
        </Box>
        <Tooltip title="Average profit per bet like this over the long run. Any single bet can still lose.">
          <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help', textAlign: 'right' }}>
            expected +{money(expectedProfit)}<br />vs {bet.reference}
          </Typography>
        </Tooltip>
      </Box>
    </Card>
  );
}

const ValueBets = () => {
  const [minEdge, setMinEdge] = useState(() => readPref('value.minEdge', 0.02));
  const [bankroll, setBankroll] = useState(() => readPref('value.bankroll', 1000));
  const [sizing, setSizing] = useState(() => readPref('value.sizing', 'quarter'));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [savingBookmakers, setSavingBookmakers] = useState(false);
  const [rescanning, setRescanning] = useState(false);
  const [outcome, setOutcome] = useState('all');

  useEffect(() => writePref('value.minEdge', minEdge), [minEdge]);
  useEffect(() => writePref('value.bankroll', bankroll), [bankroll]);
  useEffect(() => writePref('value.sizing', sizing), [sizing]);

  const { data, loading, error, reload } = useApi(`/api/value?min_edge=${minEdge}`, { deps: [minEdge] });
  const selected = useMemo(() => data?.selected_bookmakers || [], [data]);

  const bets = useMemo(
    () => (data?.bets || []).filter(b => outcome === 'all'
      || (outcome === 'clean' ? !b.suspicious : outcome === 'favourites' ? b.true_probability >= 0.5 : b.true_probability < 0.5)),
    [data, outcome]
  );

  const saveBookmakers = async (bookmakers) => {
    setSavingBookmakers(true);
    try {
      const response = await apiCall('/api/settings', { method: 'PUT', body: JSON.stringify({ bookmakers }) });
      if (!response.ok) throw new Error();
      setPickerOpen(false);
      await reload({ silent: true });
    } finally {
      setSavingBookmakers(false);
    }
  };

  // Rescans go through the arbitrage endpoint, which owns the paid scan and its rate limit
  const rescan = async () => {
    setRescanning(true);
    try {
      await apiCall('/api/arbitrage?refresh=1');
      await reload({ silent: true });
    } finally {
      setRescanning(false);
    }
  };

  const bookmakerLabel = selected.length ? `${selected.length} bookmaker${selected.length === 1 ? '' : 's'}` : 'All bookmakers';
  const flagged = (data?.bets || []).filter(b => b.suspicious).length;

  return (
    <Box>
      <PageHeader
        title="Value bets"
        subtitle="Prices that pay more than an outcome's true chance deserves, judged against Pinnacle, the sharpest bookmaker, with its margin removed. Not guaranteed winners, but profitable over many bets."
      />
      <BetsTabs />

      {loading && !data ? (
        <CardGridSkeleton count={3} height={280} />
      ) : error ? (
        <ErrorState message={error} onRetry={() => reload()} />
      ) : !data.configured ? (
        <EmptyState icon={<KeyIcon />} title="Connect a multi-bookmaker odds feed" body="Value bets use the same odds feed as arbitrage. Add an ODDS_API_KEY to the worker to switch it on." />
      ) : (
        <>
          <Card sx={{ p: 1.5, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center' }}>
            <TextField
              label="Bankroll" type="number" size="small" value={bankroll}
              onChange={(e) => setBankroll(Math.max(0, parseFloat(e.target.value) || 0))}
              inputProps={{ min: 0, inputMode: 'decimal' }}
              sx={{ width: { xs: 'calc(50% - 5px)', sm: 140 } }}
            />
            <TextField select label="Stake sizing" size="small" value={sizing} onChange={(e) => setSizing(e.target.value)} sx={{ width: { xs: 'calc(50% - 5px)', sm: 140 } }}>
              {Object.entries(SIZING).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
            </TextField>
            <TextField select label="Minimum edge" size="small" value={minEdge} onChange={(e) => setMinEdge(e.target.value)} sx={{ width: { xs: 'calc(50% - 5px)', sm: 140 } }}>
              {[0.01, 0.02, 0.03, 0.05, 0.1].map(v => <MenuItem key={v} value={v}>{Math.round(v * 100)}%</MenuItem>)}
            </TextField>
            <Button variant="outlined" color="inherit" startIcon={<BookmakerIcon />} onClick={() => setPickerOpen(true)} disabled={!data.bookmakers?.length}
              sx={{ width: { xs: 'calc(50% - 5px)', sm: 'auto' }, borderColor: 'divider', height: 40, whiteSpace: 'nowrap', px: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 14 }, '& .MuiButton-startIcon': { mr: 0.75 } }}>
              {bookmakerLabel}
            </Button>
            <Box flex={1} minWidth={{ xs: '100%', sm: 0 }} display="flex" alignItems="center" justifyContent={{ xs: 'space-between', sm: 'flex-end' }} gap={1.5}>
              <Typography variant="caption" color="text.secondary" textAlign={{ sm: 'right' }}>
                {data.scanned_at ? `Scanned ${new Date(data.scanned_at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : 'Not scanned yet'}
                <br />Pinnacle prices {data.pinnacle_coverage} of {data.events_scanned} matches
              </Typography>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={rescan} disabled={rescanning}>
                {rescanning ? 'Scanning…' : 'Rescan'}
              </Button>
            </Box>
          </Card>

          <Box display="flex" gap={1} mb={2.5} flexWrap="wrap">
            {[['all', `All (${data.bets.length})`], ['clean', `Unflagged (${data.bets.length - flagged})`], ['favourites', 'Favourites'], ['underdogs', 'Underdogs']].map(([k, label]) => (
              <Chip key={k} label={label} onClick={() => setOutcome(k)} color={outcome === k ? 'primary' : 'default'} variant={outcome === k ? 'filled' : 'outlined'} />
            ))}
          </Box>

          {bets.length === 0 ? (
            <EmptyState
              icon={<ValueIcon />}
              title={data.bets.length ? 'Nothing in this filter' : `No bets with a ${Math.round(minEdge * 100)}%+ edge right now`}
              body={selected.length ? 'Try a lower minimum edge or add more bookmakers.' : 'Try a lower minimum edge, or rescan later as prices move.'}
            />
          ) : (
            <Box sx={cardGridSx}>
              {bets.map(b => <ValueCard key={b.id} bet={b} bankroll={bankroll} sizing={sizing} />)}
            </Box>
          )}

          <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 4, bgcolor: alpha(tokens.accent, 0.08), border: `1px solid ${alpha(tokens.accent, 0.25)}` }}>
            A +5% edge means about 5 profit per 100 staked on average, over many bets. Individual bets lose often, especially underdogs.
            Kelly staking sizes bets by edge; ¼ Kelly keeps swings small. Bookmakers often limit accounts that consistently beat their prices.
          </Alert>

          <BookmakerPicker
            open={pickerOpen}
            bookmakers={data.bookmakers || []}
            selected={selected}
            saving={savingBookmakers}
            onClose={() => setPickerOpen(false)}
            onSave={saveBookmakers}
          />
        </>
      )}
    </Box>
  );
};

export default ValueBets;
